import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Divider,
  Popover,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconClipboardList,
  IconLayoutGrid,
  IconReport,
  IconSettings,
} from '@tabler/icons-react';
import {
  DASHBOARD_SUBNAV_ITEMS,
  REPORTS_SUBNAV_ITEMS,
  isDashboardHubActive,
  isDashboardSubnavActive,
  isReportsHubActive,
  isReportsSubnavActive,
  isScheduleHubActive,
  normalizePathname,
  SCHEDULE_SUBNAV_ITEMS,
  APP_ROUTES,
  type DashboardSubnavItem,
  type ReportsSubnavItem,
  type ScheduleSubnavItem,
} from '../../navigation/headerNavConfig';
import {
  SCHEDULER_NAV_PERMISSIONS,
  usePermissionContext,
} from '../../permissions/scheduler';
import { useResourcePermissionsStore } from '../../stores/zustand/resourcePermissionsStore';
import {
  getDashboardHubExpanded,
  getReportsHubExpanded,
  getScheduleHubExpanded,
  setDashboardHubExpanded,
  setReportsHubExpanded,
  setScheduleHubExpanded,
} from '../../navigation/sidebarPreference';
import { buildPortalAppUrl, openPortalUrl } from '../../utils/portalNavigation';

function dashboardNavForItem(_item: DashboardSubnavItem) {
  return SCHEDULER_NAV_PERMISSIONS.dashboardOverview;
}

function reportsNavForItem(_item: ReportsSubnavItem) {
  return SCHEDULER_NAV_PERMISSIONS.reportsLocationHealth;
}

function scheduleNavForItem(item: ScheduleSubnavItem) {
  switch (item.path) {
    case APP_ROUTES.schedule.import:
      return SCHEDULER_NAV_PERMISSIONS.scheduleImport;
    case APP_ROUTES.schedule.locations:
      return SCHEDULER_NAV_PERMISSIONS.scheduleLocations;
    case APP_ROUTES.schedule.tasks:
      return SCHEDULER_NAV_PERMISSIONS.scheduleTasks;
    case APP_ROUTES.schedule.baselines:
      return SCHEDULER_NAV_PERMISSIONS.scheduleBaselines;
    case APP_ROUTES.schedule.calendar:
      return SCHEDULER_NAV_PERMISSIONS.scheduleCalendar;
    case APP_ROUTES.schedule.resources:
      return SCHEDULER_NAV_PERMISSIONS.scheduleTasks;
    default:
      // External Issues / Hindrances uses Tasks-Gantt until a dedicated resource exists.
      if (item.id === 'issues') return SCHEDULER_NAV_PERMISSIONS.scheduleTasks;
      return null;
  }
}

export interface AppSidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onOpenSettings: () => void;
  onNavigate?: () => void;
}

function hubChevron(expanded: boolean) {
  return expanded ? (
    <IconChevronDown size={13} stroke={1.5} className="app-sidebar-hub__chevron" />
  ) : (
    <IconChevronRight size={13} stroke={1.5} className="app-sidebar-hub__chevron" />
  );
}

function sidebarLinkClass(
  active: boolean,
  options?: { nested?: boolean; nestedNoIcon?: boolean; child?: boolean }
): string {
  const parts = ['app-sidebar-link'];
  if (active) parts.push('app-sidebar-link--active');
  if (options?.nested) parts.push('app-sidebar-link--nested');
  if (options?.nestedNoIcon) parts.push('app-sidebar-link--nested-no-icon');
  if (options?.child) parts.push('app-sidebar-link--child');
  return parts.join(' ');
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed,
  onCollapsedChange,
  onOpenSettings,
  onNavigate,
}) => {
  const { pathname } = useLocation();
  const normalizedPath = normalizePathname(pathname);
  const permissionContext = usePermissionContext();
  // Subscribe to permission payload so nav re-filters after async fetch (canShowNav identity is stable).
  const permissionsStatus = useResourcePermissionsStore((s) => s.status);
  const permissionsLoadedAt = useResourcePermissionsStore((s) => s.loadedAt);
  const canShowNav = useResourcePermissionsStore((s) => s.canShowNav);

  const visibleDashboardItems = React.useMemo(
    () =>
      DASHBOARD_SUBNAV_ITEMS.filter((item) =>
        canShowNav(dashboardNavForItem(item), permissionContext)
      ),
    [canShowNav, permissionContext, permissionsStatus, permissionsLoadedAt]
  );

  const visibleReportsItems = React.useMemo(
    () =>
      REPORTS_SUBNAV_ITEMS.filter((item) =>
        canShowNav(reportsNavForItem(item), permissionContext)
      ),
    [canShowNav, permissionContext, permissionsStatus, permissionsLoadedAt]
  );

  const visibleScheduleItems = React.useMemo(
    () =>
      SCHEDULE_SUBNAV_ITEMS.filter((item) => {
        if (item.href !== undefined && !item.href) return false;
        const nav = scheduleNavForItem(item);
        return nav ? canShowNav(nav, permissionContext) : false;
      }),
    [canShowNav, permissionContext, permissionsStatus, permissionsLoadedAt]
  );

  const [dashboardHubExpanded, setDashboardHubExpandedState] = React.useState(getDashboardHubExpanded);
  const [reportsHubExpanded, setReportsHubExpandedState] = React.useState(getReportsHubExpanded);
  const [scheduleHubExpanded, setScheduleHubExpandedState] = React.useState(getScheduleHubExpanded);
  const [dashboardFlyoutOpen, setDashboardFlyoutOpen] = React.useState(false);
  const [reportsFlyoutOpen, setReportsFlyoutOpen] = React.useState(false);
  const [scheduleFlyoutOpen, setScheduleFlyoutOpen] = React.useState(false);

  const dashboardActive = isDashboardHubActive(pathname);
  const reportsActive = isReportsHubActive(pathname);
  const scheduleActive = isScheduleHubActive(pathname);
  const showDashboardChildrenInline = collapsed;
  const showReportsChildrenInline = collapsed;
  const showScheduleChildrenInline = collapsed;
  const showDashboardHub = visibleDashboardItems.length > 0;
  const showReportsHub = visibleReportsItems.length > 0;
  const showScheduleHub = visibleScheduleItems.length > 0;

  const toggleDashboardHub = () => {
    setDashboardHubExpandedState((prev) => {
      const next = !prev;
      setDashboardHubExpanded(next);
      return next;
    });
  };

  const toggleReportsHub = () => {
    setReportsHubExpandedState((prev) => {
      const next = !prev;
      setReportsHubExpanded(next);
      return next;
    });
  };

  const toggleScheduleHub = () => {
    setScheduleHubExpandedState((prev) => {
      const next = !prev;
      setScheduleHubExpanded(next);
      return next;
    });
  };

  const renderSubLink = (args: {
    to?: string | { pathname: string; search?: string };
    href?: string;
    portalPath?: string;
    label: string;
    active: boolean;
    icon?: React.ReactNode;
    nested?: boolean;
    nestedNoIcon?: boolean;
    child?: boolean;
    end?: boolean;
  }) => {
    const className = ({ isActive }: { isActive?: boolean } = {}) =>
      sidebarLinkClass(args.active || Boolean(isActive), {
        nested: args.nested,
        nestedNoIcon: args.nestedNoIcon,
        child: args.child ?? args.nested,
      });

    const content = (
      <>
        {args.icon ? <span className="app-sidebar-link__icon">{args.icon}</span> : null}
        {!collapsed ? <span className="app-sidebar-link__label">{args.label}</span> : null}
      </>
    );

    const portalHref =
      args.portalPath != null
        ? buildPortalAppUrl(args.portalPath) || args.href
        : args.href;

    const link = portalHref ? (
      <a
        href={portalHref}
        className={className()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (args.portalPath) {
            e.preventDefault();
            openPortalUrl(args.portalPath);
          }
          onNavigate?.();
        }}
      >
        {content}
      </a>
    ) : (
      <NavLink
        to={args.to ?? '/'}
        end={args.end}
        className={({ isActive }) => className({ isActive })}
        onClick={onNavigate}
      >
        {content}
      </NavLink>
    );

    if (collapsed) {
      return (
        <Tooltip key={args.label} label={args.label} position="right" withArrow>
          {link}
        </Tooltip>
      );
    }

    return <React.Fragment key={args.label}>{link}</React.Fragment>;
  };

  const dashboardSubLinks = visibleDashboardItems.map((item) =>
    renderSubLink({
      to: item.path,
      label: item.label,
      active: isDashboardSubnavActive(item.id, pathname),
      icon: <item.Icon size={14} stroke={1.5} />,
      nested: true,
    })
  );

  const reportsSubLinks = visibleReportsItems.map((item) =>
    renderSubLink({
      to: item.path,
      label: item.label,
      active: isReportsSubnavActive(item.id, pathname),
      icon: <item.Icon size={14} stroke={1.5} />,
      nested: true,
    })
  );

  const scheduleSubLinks = visibleScheduleItems.map((item) =>
    renderSubLink({
      to: item.path,
      href: item.href,
      portalPath: item.portalPath,
      label: item.label,
      active: Boolean(item.path && normalizedPath === item.path),
      icon: <item.Icon size={14} stroke={1.5} />,
      nested: true,
    })
  );

  const dashboardHubButton = (
    <UnstyledButton
      className={`app-sidebar-hub${dashboardActive ? ' app-sidebar-hub--active' : ''}`}
      onClick={() => {
        if (collapsed) {
          if (showDashboardChildrenInline) return;
          setDashboardFlyoutOpen((o) => !o);
          return;
        }
        toggleDashboardHub();
      }}
      aria-expanded={collapsed ? dashboardFlyoutOpen : dashboardHubExpanded}
      aria-label="Home"
    >
      <span className="app-sidebar-hub__icon">
        <IconLayoutGrid size={16} stroke={1.5} />
      </span>
      {!collapsed ? (
        <>
          <span className="app-sidebar-hub__label">Home</span>
          {hubChevron(dashboardHubExpanded)}
        </>
      ) : null}
    </UnstyledButton>
  );

  const reportsHubButton = (
    <UnstyledButton
      className={`app-sidebar-hub${reportsActive ? ' app-sidebar-hub--active' : ''}`}
      onClick={() => {
        if (collapsed) {
          if (showReportsChildrenInline) return;
          setReportsFlyoutOpen((o) => !o);
          return;
        }
        toggleReportsHub();
      }}
      aria-expanded={collapsed ? reportsFlyoutOpen : reportsHubExpanded}
      aria-label="Reports"
    >
      <span className="app-sidebar-hub__icon">
        <IconReport size={16} stroke={1.5} />
      </span>
      {!collapsed ? (
        <>
          <span className="app-sidebar-hub__label">Reports</span>
          {hubChevron(reportsHubExpanded)}
        </>
      ) : null}
    </UnstyledButton>
  );

  const scheduleHubButton = (
    <UnstyledButton
      className={`app-sidebar-hub${scheduleActive ? ' app-sidebar-hub--active' : ''}`}
      onClick={() => {
        if (collapsed) {
          if (showScheduleChildrenInline) return;
          setScheduleFlyoutOpen((o) => !o);
          return;
        }
        toggleScheduleHub();
      }}
      aria-expanded={collapsed ? scheduleFlyoutOpen : scheduleHubExpanded}
      aria-label="Project Schedule"
    >
      <span className="app-sidebar-hub__icon">
        <IconClipboardList size={16} stroke={1.5} />
      </span>
      {!collapsed ? (
        <>
          <span className="app-sidebar-hub__label">Project Schedule</span>
          {hubChevron(scheduleHubExpanded)}
        </>
      ) : null}
    </UnstyledButton>
  );

  return (
    <Box className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}>
      <Stack gap={2} className="app-sidebar__nav" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {showDashboardHub ? (
          collapsed && showDashboardChildrenInline ? (
          <Stack gap={2}>
            {/* Home group: no collapsed divider — leave blank spacing only */}
            {dashboardSubLinks}
          </Stack>
        ) : collapsed ? (
          <Popover
            opened={dashboardFlyoutOpen}
            onChange={setDashboardFlyoutOpen}
            position="right-start"
            withArrow
            shadow="md"
            width={200}
          >
            <Popover.Target>
              <Tooltip
                label="Home"
                position="right"
                withArrow
                disabled={dashboardFlyoutOpen}
              >
                <Box component="span" style={{ display: 'block', width: '100%' }}>
                  {dashboardHubButton}
                </Box>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown p="xs">
              <Stack gap={4}>
                <Text className="app-sidebar-group-label" mb={2}>
                  Home
                </Text>
                {visibleDashboardItems.map((item) => {
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className={({ isActive }) =>
                        sidebarLinkClass(isDashboardSubnavActive(item.id, pathname) || isActive, {
                          child: true,
                        })
                      }
                      onClick={() => {
                        setDashboardFlyoutOpen(false);
                        onNavigate?.();
                      }}
                    >
                      <span className="app-sidebar-link__icon">
                        <item.Icon size={14} stroke={1.5} />
                      </span>
                      <span className="app-sidebar-link__label">{item.label}</span>
                    </NavLink>
                  );
                })}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        ) : (
          <>
            {dashboardHubButton}
            <Collapse in={dashboardHubExpanded}>
              <Stack gap={2} mt={2}>
                {dashboardSubLinks}
              </Stack>
            </Collapse>
          </>
        )
        ) : null}

        {showScheduleHub ? (
          collapsed && showScheduleChildrenInline ? (
          <Stack gap={2}>
            <div className="app-sidebar-collapsed-group-divider" aria-hidden />
            {scheduleSubLinks}
          </Stack>
        ) : collapsed ? (
          <Popover
            opened={scheduleFlyoutOpen}
            onChange={setScheduleFlyoutOpen}
            position="right-start"
            withArrow
            shadow="md"
            width={240}
          >
            <Popover.Target>
              <Tooltip
                label="Project Schedule"
                position="right"
                withArrow
                disabled={scheduleFlyoutOpen}
              >
                <Box component="span" style={{ display: 'block', width: '100%' }}>
                  {scheduleHubButton}
                </Box>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown p="xs">
              <Stack gap={4}>
                <Text className="app-sidebar-group-label" mb={2}>
                  Project Schedule
                </Text>
                {visibleScheduleItems.map((item) => {
                  const className = sidebarLinkClass(
                    Boolean(item.path && normalizedPath === item.path),
                    { child: true }
                  );
                  const onClick = () => {
                    setScheduleFlyoutOpen(false);
                    onNavigate?.();
                  };
                  const body = (
                    <>
                      <span className="app-sidebar-link__icon">
                        <item.Icon size={14} stroke={1.5} />
                      </span>
                      <span className="app-sidebar-link__label">{item.label}</span>
                    </>
                  );
                  if (item.href || item.portalPath) {
                    const portalHref =
                      item.portalPath != null
                        ? buildPortalAppUrl(item.portalPath) || item.href
                        : item.href;
                    return (
                      <a
                        key={item.id}
                        href={portalHref}
                        className={className}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (item.portalPath) {
                            e.preventDefault();
                            openPortalUrl(item.portalPath);
                          }
                          onClick();
                        }}
                      >
                        {body}
                      </a>
                    );
                  }
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path ?? '/'}
                      className={({ isActive }) =>
                        sidebarLinkClass(Boolean(item.path && (normalizedPath === item.path || isActive)), {
                          child: true,
                        })
                      }
                      onClick={onClick}
                    >
                      {body}
                    </NavLink>
                  );
                })}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        ) : (
          <>
            {scheduleHubButton}
            <Collapse in={scheduleHubExpanded}>
              <Stack gap={2} mt={2}>
                {scheduleSubLinks}
              </Stack>
            </Collapse>
          </>
        )
        ) : null}

        {showReportsHub ? (
          collapsed && showReportsChildrenInline ? (
          <Stack gap={2}>
            <div className="app-sidebar-collapsed-group-divider" aria-hidden />
            {reportsSubLinks}
          </Stack>
        ) : collapsed ? (
          <Popover
            opened={reportsFlyoutOpen}
            onChange={setReportsFlyoutOpen}
            position="right-start"
            withArrow
            shadow="md"
            width={240}
          >
            <Popover.Target>
              <Tooltip
                label="Reports"
                position="right"
                withArrow
                disabled={reportsFlyoutOpen}
              >
                <Box component="span" style={{ display: 'block', width: '100%' }}>
                  {reportsHubButton}
                </Box>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown p="xs">
              <Stack gap={4}>
                <Text className="app-sidebar-group-label" mb={2}>
                  Reports
                </Text>
                {visibleReportsItems.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    className={({ isActive }) =>
                      sidebarLinkClass(isReportsSubnavActive(item.id, pathname) || isActive, {
                        child: true,
                      })
                    }
                    onClick={() => {
                      setReportsFlyoutOpen(false);
                      onNavigate?.();
                    }}
                  >
                    <span className="app-sidebar-link__icon">
                      <item.Icon size={14} stroke={1.5} />
                    </span>
                    <span className="app-sidebar-link__label">{item.label}</span>
                  </NavLink>
                ))}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        ) : (
          <>
            {reportsHubButton}
            <Collapse in={reportsHubExpanded}>
              <Stack gap={2} mt={2}>
                {reportsSubLinks}
              </Stack>
            </Collapse>
          </>
        )
        ) : null}
      </Stack>

      <Box className="app-sidebar__footer">
        <Stack gap={2} mb="sm">
          <Tooltip
            label={collapsed ? 'Expand panel' : 'Collapse panel'}
            position="right"
            withArrow
            disabled={!collapsed}
          >
            <UnstyledButton
              className="app-sidebar-link"
              onClick={() => onCollapsedChange(!collapsed)}
              aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
            >
              <span className="app-sidebar-link__icon">
                {collapsed ? (
                  <IconChevronsRight size={16} stroke={1.5} />
                ) : (
                  <IconChevronsLeft size={16} stroke={1.5} />
                )}
              </span>
              {!collapsed ? (
                <span className="app-sidebar-link__label">Collapse panel</span>
              ) : null}
            </UnstyledButton>
          </Tooltip>
        </Stack>
        <Divider mb="sm" />
        <Stack gap={2}>
          {collapsed ? (
            <Tooltip label="App Settings" position="right" withArrow>
              <UnstyledButton
                className="app-sidebar-link app-sidebar-link--footer"
                onClick={onOpenSettings}
                aria-label="App Settings"
              >
                <span className="app-sidebar-link__icon">
                  <IconSettings size={16} stroke={1.5} />
                </span>
              </UnstyledButton>
            </Tooltip>
          ) : (
            <UnstyledButton className="app-sidebar-link app-sidebar-link--footer" onClick={onOpenSettings}>
              <span className="app-sidebar-link__icon">
                <IconSettings size={16} stroke={1.5} />
              </span>
              <span className="app-sidebar-link__label">App Settings</span>
            </UnstyledButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default AppSidebar;
